import React from 'react';
import { motion } from 'framer-motion';
import { ErrorMessage } from './ErrorMessage';
import { SuccessMessage } from './SuccessMessage';
import { NextStepsList } from './NextStepsList';
import { SQLGenerationSection } from './SQLGenerationSection';
import { SQLConfirmationCheckbox } from './SQLConfirmationCheckbox';
import { LoadingButton } from './LoadingButton';

interface SQLSetupViewProps {
  accountIdentifier: string;
  completeSql: string;
  error: string | null;
  successMessage: string | null;
  testingConnection: boolean;
  hasSqlCompleted: boolean;
  onEdit: () => void;
  onTestConnection: () => void;
}

/**
 * SQLSetupView Component
 *
 * Displays the SQL generation and connection testing section for managed key authentication.
 *
 * @component
 * @example
 * ```tsx
 * <SQLSetupView
 *   accountIdentifier="myorg-myaccount"
 *   completeSql="USE ROLE SECURITYADMIN;..."
 *   error={null}
 *   successMessage={null}
 *   testingConnection={false}
 *   hasSqlCompleted={true}
 *   onEdit={handleEdit}
 *   onTestConnection={handleTestConnection}
 * />
 * ```
 */
export const SQLSetupView: React.FC<SQLSetupViewProps> = ({
  accountIdentifier,
  completeSql,
  error,
  successMessage,
  testingConnection,
  hasSqlCompleted,
  onEdit,
  onTestConnection,
}) => {
  return (
    <motion.div className="space-y-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg text-dataops-dark">Setup SQL Commands</h3>
          <p className="mt-2 text-sm text-secondary-text">
            Account validated for <span className="font-medium text-primary-text">{accountIdentifier}</span>
          </p>
        </div>
        <motion.button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm rounded-md text-primary-text bg-white hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue transition-all duration-150"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-label="Edit account identifier"
        >
          Edit
        </motion.button>
      </div>

      <NextStepsList accountIdentifier={accountIdentifier} />

      <SQLGenerationSection
        sqlCode={completeSql}
        title="Complete Setup SQL"
        description="Copy and run all these commands in your Snowflake account"
      />

      <SQLConfirmationCheckbox label="I have run this SQL" disabled={testingConnection} />

      {successMessage !== null && <SuccessMessage message={successMessage} />}
      {error !== null && <ErrorMessage error={error} accountIdentifier={accountIdentifier} />}
      <div className="flex justify-end pt-4">
        <LoadingButton
          onClick={onTestConnection}
          disabled={testingConnection || !hasSqlCompleted}
          loading={testingConnection}
          loadingText="Testing Connection..."
          ariaLabel={testingConnection ? 'Testing Snowflake connection' : 'Test Snowflake connection'}
        >
          Test Connection
        </LoadingButton>
      </div>
    </motion.div>
  );
};
